import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { UserContractor } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface AdminUserContractorGetParams {
  limit: number;
  offset: number;
  sort: string;
  contractorId: number;
  projectId: number;
  search: string;
  isActive: boolean;
}

interface RequestParams {
  params?: Partial<AdminUserContractorGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<UserContractor>>,
    'queryKey' | 'queryFn'
  >;
}

const requiredParams: Partial<keyof AdminUserContractorGetParams>[] = ['limit', 'offset']

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  contractorId: 'number',
  projectId: 'number',
  search: 'string',
  isActive: 'boolean',
}

export const useUserContractorListRequest = ({
  params,
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params || {})

  const queryParams = buildRequestParams<Partial<AdminUserContractorGetParams>>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<UserContractor>>(
    [Entities.UserContractors, JSON.stringify(transformedParams)],
    async () => {
      const {
        limit,
        offset,
        sort,
        contractorId,
        projectId,
        search,
        isActive,
      } = queryParams
      const { data } = await api.v1AdminUsersUsersContractorsGet(
        limit,
        offset,
        sort,
        contractorId,
        projectId,
        search,
        isActive,
      )
      return data
    },
    {
      enabled: requiredParams.every((param) => transformedParams[param] !== undefined),
      ...options,
    },
  )
}
